import React, { useEffect, useState } from 'react';
import { masterSettingsAPI } from '@/api/api';
import { Plus, Edit, Trash2, Search, Percent, Tag, TrendingDown, BookOpen, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';

const Discounts = () => {
  const [discounts, setDiscounts] = useState([]);
  const [courses, setCourses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTypeFilter, setSelectedTypeFilter] = useState('');
  const [selectedCourseFilter, setSelectedCourseFilter] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentDiscount, setCurrentDiscount] = useState(null);
  const [entryTypeFilter, setEntryTypeFilter] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    type: 'Percentage',
    value: '',
    course_id: '',
    course_name: '',
    session_id: '',
    session_name: '',
    is_lateral_entry: false,
    entry_type: 'Regular'
  });

  useEffect(() => {
    fetchDiscounts();
    fetchCourses();
  }, []);

  // Fetch sessions when course changes
  useEffect(() => {
    if (formData.course_id) {
      fetchSessionsForCourse(formData.course_id);
    } else {
      setSessions([]);
    }
  }, [formData.course_id]);

  const fetchCourses = async () => {
    try {
      const response = await masterSettingsAPI.getCourses();
      setCourses(response.data || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchSessionsForCourse = async (courseId) => {
    try {
      const response = await masterSettingsAPI.getSessions();
      // Filter sessions for the selected course or show all if course has sessions
      const allSessions = response.data || [];
      // For now, show all sessions - can be filtered by course if needed
      setSessions(allSessions);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    }
  };

  const fetchDiscounts = async () => {
    try {
      const response = await masterSettingsAPI.getDiscounts();
      setDiscounts(response.data);
    } catch (error) {
      console.error('Error fetching discounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.course_id) {
      alert('Please select a course');
      return;
    }
    if (!formData.session_id) {
      alert('Please select a session');
      return;
    }
    
    try {
      const selectedCourse = courses.find(c => c.id === formData.course_id);
      const selectedSession = sessions.find(s => s.id === formData.session_id);
      
      const submitData = {
        ...formData,
        value: parseFloat(formData.value),
        course_name: selectedCourse?.name || '',
        session_name: selectedSession?.name || ''
      };
      
      if (editMode) {
        await masterSettingsAPI.updateDiscount(currentDiscount.id, submitData);
      } else {
        await masterSettingsAPI.createDiscount(submitData);
      }
      setIsDialogOpen(false);
      setEditMode(false);
      setCurrentDiscount(null);
      setFormData({ name: '', type: 'Percentage', value: '', course_id: '', course_name: '', session_id: '', session_name: '', is_lateral_entry: false, entry_type: 'Regular' });
      fetchDiscounts();
    } catch (error) {
      console.error('Error saving discount:', error);
      alert('Failed to save discount');
    }
  };

  const handleEdit = (discount) => {
    setCurrentDiscount(discount);
    setFormData({
      name: discount.name,
      type: discount.type,
      value: discount.value.toString(),
      course_id: discount.course_id || '',
      course_name: discount.course_name || '',
      session_id: discount.session_id || '',
      session_name: discount.session_name || '',
      is_lateral_entry: discount.is_lateral_entry || false,
      entry_type: discount.entry_type || (discount.is_lateral_entry ? 'Lateral' : 'Regular')
    });
    // Fetch sessions for the course if editing
    if (discount.course_id) {
      fetchSessionsForCourse(discount.course_id);
    }
    setEditMode(true);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this discount?')) {
      try {
        await masterSettingsAPI.deleteDiscount(id);
        fetchDiscounts();
      } catch (error) {
        console.error('Error deleting discount:', error);
        alert('Failed to delete discount');
      }
    }
  };

  const filteredDiscounts = discounts.filter(discount => {
    const matchesSearch = discount.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = selectedTypeFilter === '' || discount.type === selectedTypeFilter;
    const matchesCourse = selectedCourseFilter === '' || discount.course_id === selectedCourseFilter;
    const entryType = discount.entry_type || (discount.is_lateral_entry ? 'Lateral' : 'Regular');
    const matchesEntry = entryTypeFilter === '' || entryType === entryTypeFilter;
    return matchesSearch && matchesType && matchesCourse && matchesEntry;
  });

  // Group by course
  const groupedByCourse = filteredDiscounts.reduce((acc, discount) => {
    const courseKey = discount.course_name || 'Uncategorized';
    if (!acc[courseKey]) {
      acc[courseKey] = [];
    }
    acc[courseKey].push(discount);
    return acc;
  }, {});

  // Group by type
  const percentageDiscounts = filteredDiscounts.filter(d => d.type === 'Percentage');
  const fixedDiscounts = filteredDiscounts.filter(d => d.type === 'Fixed');

  // Calculate statistics
  const totalDiscounts = discounts.length;
  const activeDiscounts = discounts.filter(d => d.is_active).length;
  const avgValue = discounts.length > 0 
    ? discounts.reduce((sum, d) => sum + d.value, 0) / discounts.length 
    : 0;

  const formatDiscountValue = (discount) => {
    if (discount.type === 'Percentage') {
      return `${discount.value}%`;
    } else {
      return `₹${discount.value.toLocaleString('en-IN')}`;
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64" data-testid="loading">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="discounts-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Discounts Management</h1>
          <p className="text-gray-600 mt-1">Manage course-wise discount policies for fee structures</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) {
            setEditMode(false);
            setCurrentDiscount(null);
            setFormData({ name: '', type: 'Percentage', value: '', course_id: '', course_name: '', session_id: '', session_name: '', is_lateral_entry: false, entry_type: 'Regular' });
            setSessions([]);
          }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-discount-button">
              <Plus size={20} className="mr-2" /> Add Discount
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="discount-dialog" className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editMode ? 'Edit Discount' : 'Add New Discount'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-3">
              {/* Course & Session in one row */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="course" className="text-sm">Course *</Label>
                  <select
                    id="course"
                    className="w-full h-9 border border-gray-300 rounded-md px-2 text-sm"
                    value={formData.course_id}
                    onChange={(e) => {
                      const course = courses.find(c => c.id === e.target.value);
                      setFormData({ 
                        ...formData, 
                        course_id: e.target.value,
                        course_name: course?.name || '',
                        session_id: '',
                        session_name: ''
                      });
                    }}
                    required
                  >
                    <option value="">-- Select --</option>
                    {courses.map(course => (
                      <option key={course.id} value={course.id}>{course.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="session" className="text-sm">Session *</Label>
                  <select
                    id="session"
                    className="w-full h-9 border border-gray-300 rounded-md px-2 text-sm"
                    value={formData.session_id}
                    onChange={(e) => {
                      const session = sessions.find(s => s.id === e.target.value);
                      setFormData({ 
                        ...formData, 
                        session_id: e.target.value,
                        session_name: session?.name || ''
                      });
                    }}
                    required
                    disabled={!formData.course_id}
                  >
                    <option value="">-- Select --</option>
                    {sessions.map(session => (
                      <option key={session.id} value={session.id}>{session.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <Label htmlFor="name" className="text-sm">Discount Name *</Label>
                <Input
                  id="name"
                  className="h-9"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Early Bird Discount"
                  required
                  data-testid="discount-name-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="type" className="text-sm">Type *</Label>
                  <select
                    id="type"
                    className="w-full h-9 border border-gray-300 rounded-md px-2 text-sm"
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value, value: '' })}
                    required
                    data-testid="discount-type-select"
                  >
                    <option value="Percentage">Percentage (%)</option>
                    <option value="Fixed">Fixed (₹)</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="value" className="text-sm">
                    {formData.type === 'Percentage' ? 'Value (%)' : 'Amount (₹)'} *
                  </Label>
                  <Input
                    id="value"
                    className="h-9"
                    type="number"
                    min="0"
                    max={formData.type === 'Percentage' ? '100' : undefined}
                    value={formData.value}
                    onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                    placeholder={formData.type === 'Percentage' ? '0-100' : '0.00'}
                    required
                    data-testid="discount-value-input"
                  />
                </div>
              </div>

              <div>
                <Label className="text-sm">Entry Type *</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, entry_type: 'Regular', is_lateral_entry: false })}
                    className={`p-2 border-2 rounded-lg text-center text-sm transition-colors ${
                      formData.entry_type === 'Regular'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 hover:border-blue-300'
                    }`}
                  >
                    Regular
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, entry_type: 'Lateral', is_lateral_entry: true })}
                    className={`p-2 border-2 rounded-lg text-center text-sm transition-colors ${
                      formData.entry_type === 'Lateral'
                        ? 'border-orange-500 bg-orange-50 text-orange-700'
                        : 'border-gray-300 hover:border-orange-300'
                    }`}
                  >
                    Lateral
                  </button>
                </div>
              </div>

              <Button type="submit" className="w-full h-9" data-testid="submit-discount-button">
                {editMode ? 'Update Discount' : 'Create Discount'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 text-gray-400" size={20} />
            <Input
              placeholder="Search discounts by name..."
              className="pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="search-discounts-input"
            />
          </div>
          
          <div>
            <select
              className="w-full h-10 border border-gray-300 rounded-md px-3"
              value={selectedCourseFilter}
              onChange={(e) => setSelectedCourseFilter(e.target.value)}
            >
              <option value="">All Courses</option>
              {courses.map(course => (
                <option key={course.id} value={course.id}>{course.name}</option>
              ))}
            </select>
          </div>

          <div>
            <select
              className="w-full h-10 border border-gray-300 rounded-md px-3"
              value={selectedTypeFilter}
              onChange={(e) => setSelectedTypeFilter(e.target.value)}
              data-testid="filter-type-select"
            >
              <option value="">All Types</option>
              <option value="Percentage">Percentage Discounts</option>
              <option value="Fixed">Fixed Amount Discounts</option>
            </select>
          </div>

          <div>
            <select
              className="w-full h-10 border border-gray-300 rounded-md px-3"
              value={entryTypeFilter}
              onChange={(e) => setEntryTypeFilter(e.target.value)}
            >
              <option value="">All Entry Types</option>
              <option value="Regular">Regular Entry</option>
              <option value="Lateral">Lateral Entry</option>
            </select>
          </div>
        </div>
      </div>

      {/* Discounts grouped by Course */}
      <div className="space-y-6">
        {Object.keys(groupedByCourse).length > 0 ? (
          Object.entries(groupedByCourse).map(([courseName, courseDiscounts]) => (
            <div key={courseName} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-blue-600 p-2 rounded-lg">
                      <BookOpen size={24} className="text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-800">{courseName}</h2>
                      <p className="text-sm text-gray-600 mt-1">{courseDiscounts.length} discount(s)</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-6">
                {courseDiscounts.map((discount) => (
                  <div 
                    key={discount.id} 
                    className={`relative group border-2 rounded-xl p-5 hover:shadow-lg transition-all ${
                      discount.type === 'Percentage' 
                        ? 'border-purple-200 hover:border-purple-400' 
                        : 'border-green-200 hover:border-green-400'
                    }`}
                    data-testid={`discount-card-${discount.id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className={`p-3 rounded-lg ${discount.type === 'Percentage' ? 'bg-purple-100' : 'bg-green-100'}`}>
                        {discount.type === 'Percentage' ? (
                          <Percent size={28} className="text-purple-600" />
                        ) : (
                          <Tag size={28} className="text-green-600" />
                        )}
                      </div>
                      <div className="flex flex-col gap-1">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          discount.is_active 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {discount.is_active ? 'Active' : 'Inactive'}
                        </span>
                        {discount.session_name && (
                          <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                            {discount.session_name}
                          </span>
                        )}
                      </div>
                    </div>

                    <h3 className="font-bold text-gray-800 text-lg mb-2">{discount.name}</h3>
                    
                    <div className={`rounded-lg p-3 mb-3 ${discount.type === 'Percentage' ? 'bg-purple-50' : 'bg-green-50'}`}>
                      <p className={`text-3xl font-bold ${discount.type === 'Percentage' ? 'text-purple-600' : 'text-green-600'}`}>
                        {discount.type === 'Percentage' ? `${discount.value}%` : `₹${discount.value.toLocaleString('en-IN')}`}
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        {discount.type === 'Percentage' ? 'Percentage off' : 'Fixed discount'}
                      </p>
                    </div>

                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span className={`px-2 py-1 rounded ${
                        discount.entry_type === 'Lateral' 
                          ? 'bg-orange-100 text-orange-700' 
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {discount.entry_type || 'Regular'}
                      </span>
                    </div>

                    {/* Action Buttons */}
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                      <button 
                        onClick={() => handleEdit(discount)}
                        className="p-2 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg transition-colors"
                        title="Edit"
                        data-testid={`edit-discount-${discount.id}`}
                      >
                        <Edit size={16} />
                      </button>
                      <button 
                        onClick={() => handleDelete(discount.id)}
                        className="p-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg transition-colors"
                        title="Delete"
                        data-testid={`delete-discount-${discount.id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        ) : (
          <div className="bg-white p-12 rounded-xl shadow-sm border border-gray-200 text-center">
            <TrendingDown size={64} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 mb-2">No discounts found.</p>
            <p className="text-sm text-gray-400">Create your first discount to help students save on fees.</p>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-blue-50 p-6 rounded-xl border border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-600 text-sm font-medium">Total Discounts</p>
              <p className="text-3xl font-bold text-blue-700 mt-2" data-testid="total-discounts-stat">{totalDiscounts}</p>
            </div>
            <Tag className="text-blue-400" size={40} />
          </div>
        </div>
        
        <div className="bg-green-50 p-6 rounded-xl border border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-600 text-sm font-medium">Active Discounts</p>
              <p className="text-3xl font-bold text-green-700 mt-2">{activeDiscounts}</p>
            </div>
            <TrendingDown className="text-green-400" size={40} />
          </div>
        </div>
        
        <div className="bg-purple-50 p-6 rounded-xl border border-purple-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-600 text-sm font-medium">Percentage Type</p>
              <p className="text-3xl font-bold text-purple-700 mt-2">{percentageDiscounts.length}</p>
            </div>
            <Percent className="text-purple-400" size={40} />
          </div>
        </div>
        
        <div className="bg-orange-50 p-6 rounded-xl border border-orange-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-600 text-sm font-medium">Fixed Type</p>
              <p className="text-3xl font-bold text-orange-700 mt-2">{fixedDiscounts.length}</p>
            </div>
            <div className="text-orange-400 text-2xl font-bold">💰</div>
          </div>
        </div>
      </div>

      {/* Quick Insights */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-xl border border-purple-200">
          <h3 className="font-semibold text-gray-800 mb-2">Average Savings</h3>
          <p className="text-sm text-gray-600">
            On average, discounts provide <span className="font-bold text-purple-600">{avgValue.toFixed(1)}% or ₹{avgValue.toFixed(0)}</span> savings
          </p>
        </div>
        
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl border border-green-200">
          <h3 className="font-semibold text-gray-800 mb-2">Discount Types</h3>
          <p className="text-sm text-gray-600">
            <span className="font-bold text-green-600">{percentageDiscounts.length}</span> percentage + 
            <span className="font-bold text-green-600"> {fixedDiscounts.length}</span> fixed discounts
          </p>
        </div>
        
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-200">
          <h3 className="font-semibold text-gray-800 mb-2">Active Status</h3>
          <p className="text-sm text-gray-600">
            <span className="font-bold text-blue-600">{activeDiscounts}/{totalDiscounts}</span> discounts are currently active
          </p>
        </div>
      </div>
    </div>
  );
};

export default Discounts;
